import { Select } from 'antd'
import { t } from 'i18next'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useUsers } from '../hooks'
import { User } from '../store/reducers/userReducer'

interface SelectOption {
  key: string
  value: string
  label: string
  disabled?: boolean
}

interface SelectParticpantProps {
  onChange?: (value: string[] | undefined) => void
  disabled?: boolean
  initialParticipants?: string[]
  allParticipants?: User[]
  disableRemove?: boolean
  loading?: boolean
}

const SelectParticipant = memo(
  ({
    onChange,
    disabled,
    initialParticipants,
    allParticipants,
    disableRemove,
    loading: loadingFromProps,
  }: SelectParticpantProps) => {
    const { userList: allUsers, loading } = useUsers({})
    const [participants, setParticipants] = useState<string[]>([])

    const users = useMemo(() => {
      return allParticipants && allParticipants.length > 0
        ? allParticipants
        : allUsers
    }, [allParticipants, allUsers])

    const options: SelectOption[] = useMemo(() => {
      const baseUsers = users ?? []
      const baseOptions = baseUsers.map((user: User) => ({
        key: user.id,
        value: user.id,
        label: user.displayName,
        ...(disableRemove && {
          disabled: initialParticipants?.includes(user.id),
        }),
      }))

      // handle case where the user is not in the baseOptions of the datasource
      const missingUsersIds =
        initialParticipants?.filter(
          (id) => !baseUsers.some((user) => user.id === id)
        ) || []

      const baseUserIds = new Set(baseUsers.map((u) => u.id))

      const missingOptions = missingUsersIds
        ?.filter((id) => !baseUserIds.has(id))
        .map((id) => {
          const user = allUsers?.find((u) => u.id === id)
          return user
            ? {
                key: user.id,
                value: user.id,
                label: user.displayName,
                ...(disableRemove && { disabled: true }),
              }
            : null
        })
        .filter(Boolean) as SelectOption[]

      return [...baseOptions, ...missingOptions]
    }, [users, disableRemove, initialParticipants, allUsers])

    const participantLabels = useMemo(() => {
      return participants.map((id) => {
        const option = options.find((opt) => opt.value === id)
        return option
          ? { value: id, label: option.label }
          : { value: id, label: id }
      })
    }, [participants, options])

    const handleChange = useCallback(
      (value: any[]) => {
        const ids = value.map((v) => v.value)
        setParticipants(ids)
        if (onChange) {
          onChange(ids)
        }
      },
      [onChange]
    )

    useEffect(() => {
      setParticipants(initialParticipants || [])
    }, [initialParticipants])

    return (
      <Select
        mode="multiple"
        size="large"
        style={{ width: '100%' }}
        options={options}
        disabled={disabled}
        value={participantLabels}
        labelInValue
        loading={loading || loadingFromProps}
        onChange={handleChange}
        optionFilterProp="label"
        notFoundContent={loading ? t('loading') : t('noUsersFound')}
      />
    )
  }
)

export default SelectParticipant
